import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import useApi from '../../hooks/useApi'
import ProfileTemplate from './ProfileTemplate'
import { ProfileMain } from '../../components/Profiles'
import { UserProfileLeftSidebar } from '../../components/Profiles/UserProfile'
import Loading from '../../components/loading'

// build socialLinks for ProfileHeader from user/business object
function buildSocialLinksFromUser(obj) {
  if (!obj) return []
  const platforms = [
    { key: 'facebook_link', platform: 'facebook' },
    { key: 'instagram_link', platform: 'instagram' },
    { key: 'linkedin_link', platform: 'linkedin' },
    { key: 'youtube_link', platform: 'youtube' },
    { key: 'tiktok_link', platform: 'tiktok' },
  ]
  return platforms
    .filter(({ key }) => obj[key])
    .map(({ platform, key }) => ({ platform, href: obj[key], label: platform }))
}

// current user's profile page.

export default function UserProfilePage() {
  const { t, i18n } = useTranslation()
  const { currentUser } = useCurrentUser()
  const {
    getCurrentUser,
    getCurrentUserGroups,
    getGroup,
    getUserFollowers,
    getTodaysEvents,
    getMonthlyEvents,
  } = useApi()

  const [user, setUser] = useState(null)
  const [groups, setGroups] = useState([])
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [followersCount, setFollowersCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const userId = currentUser?.id

  useEffect(() => {
    if (!userId) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        const [userRes, groupsRes, followersRes, todayRes, monthRes] = await Promise.allSettled([
          getCurrentUser(),
          getCurrentUserGroups(),
          getUserFollowers({ id: userId }),
          getTodaysEvents(),
          getMonthlyEvents(),
        ])
        if (cancelled) return
        if (userRes.status === 'fulfilled' && userRes.value?.data) {
          setUser(userRes.value.data)
        } else if (currentUser) {
          setUser(currentUser)
        }
        if (groupsRes.status === 'fulfilled' && groupsRes.value?.data) {
          const rawGroups = groupsRes.value.data
          const enriched = await Promise.all(
            rawGroups.map(async (g) => {
              try {
                const { data } = await getGroup({ id: g.slug ?? g.id })
                return { ...g, user_count: data?.user_count ?? g.user_count }
              } catch {
                return g
              }
            })
          )
          if (!cancelled) setGroups(enriched)
        }
        let count = 0
        if (followersRes.status === 'fulfilled') {
          const data = followersRes.value?.data
          if (Array.isArray(data)) {
            count = data.length
          } else if (typeof data?.count === 'number') {
            count = data.count
          } else if (Array.isArray(data?.followers)) {
            count = data.followers.length
          }
        }
        if (count === 0 && userRes.status === 'fulfilled' && typeof userRes.value?.data?.followers_count === 'number') {
          count = userRes.value.data.followers_count
        }
        if (!cancelled) setFollowersCount(count)
        const today = todayRes.status === 'fulfilled' ? todayRes.value?.data ?? [] : []
        const month = monthRes.status === 'fulfilled' ? monthRes.value?.data ?? [] : []
        const combined = [...(Array.isArray(today) ? today : []), ...(Array.isArray(month) ? month : [])]
        if (!cancelled) setUpcomingEvents(combined.slice(0, 10))
      } catch (err) {
        if (!cancelled && currentUser) setUser(currentUser)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // REVISIT: intentionally depend only on userId so we fetch once per user. API functions from useApi()
    // are not in the dependency array to avoid re-running when their refs change every render.
  }, [userId])

  if (isLoading) {
    return <Loading show />
  }

  const displayUser = user ?? currentUser
  const avatarUrl =
    displayUser?.media_attachment?.thumbnail_url ??
    displayUser?.media_attachment?.direct_upload_url ??
    '/images/default-user.png'
  const subtitle = displayUser?.nickname ? `@${displayUser.nickname}` : null

  // cover: use user's cover if set, else default image (can be changed via Cover Photos button)
  const coverImageUrl =
    displayUser?.cover_photo?.thumbnail_url ??
    displayUser?.cover_photo?.direct_upload_url ??
    undefined

  // social links: only show icons for platforms user has added (user account doesn't have these yet; business does)
  const socialLinks = buildSocialLinksFromUser(displayUser)

  return (
    <ProfileTemplate
      profileType="user"
      headerProps={{
        name: displayUser?.name ?? null,
        avatarUrl,
        subtitle,
        coverImageUrl,
        onCoverPhotosClick: () => {
          // TODO: open cover photo modal or navigate to settings when flow is ready
        },
        onShareClick: () => {},
        socialLinks,
      }}
      breadcrumbItems={[
        { label: t('profiles.profile', 'Profile'), href: '/profile' },
        { label: displayUser?.name ?? t('profiles.you', 'You') },
      ]}
      leftSidebarContent={
        <UserProfileLeftSidebar
          name={displayUser?.name}
          location={displayUser?.location}
          quote={displayUser?.quote ?? displayUser?.user_quote ?? displayUser?.favorite_quote}
          quoteAuthor={displayUser?.quote_author ?? displayUser?.favorite_quote_author}
          followersCount={followersCount}
          onEditCommunity={undefined}
          favoriteCharity={displayUser?.favorite_charity}
          categories={displayUser?.categories}
          groups={groups}
          upcomingEvents={upcomingEvents}
          awards={[]}
          locale={displayUser?.language ?? i18n?.language ?? 'en'}
        />
      }
      mainContent={<ProfileMain location={displayUser?.location} />}
      rightSidebarContent={null}
    />
  )
}
